/**
 * TicketCard — card de ticket do Kanban do Help Desk.
 *
 * TRÊS densidades, transcritas dos mockups CORRIGIDOS em
 * DOCS/superpowers/prototypes/2026-07-26-kanban-cards-v3/:
 *   'detailed' → Helpdesk 01 "Ficha do cliente"   (comparacao.html:1181-1207)
 *   'normal'   → Helpdesk 07 "Relógio de etapa"   (helpdesk.html:939-956)
 *   'compact'  → Helpdesk 05 "Linha de fila"      (comparacao.html:1357-1371)
 *
 * O corpo 'normal' mede ESTAGNAÇÃO, não contrato: tempo desde `stageEnteredAt`
 * contra `alertConfig` da etapa (prop `stageAlertClock`, resolvida pela cascata
 * etapa → pipeline → sistema). Ele NÃO lê `sla.clocks`, `slaDueAt` nem
 * `slaBreached` — o texto do mockup é explícito (helpdesk.html:931) e a regra
 * está no cabeçalho de DOCS/superpowers/plans/2026-07-28-sla-c-superficies-helpdesk.md.
 *
 * Contrato do card (cards-v3.css §5, `.c3-card`): 284px de largura útil, padding
 * 14, radius 12, borda fio-de-cabelo de 1px, FLAT (elevação é a borda — nunca
 * sombra), hover = a borda escurece. A margem entre cards NÃO vive aqui: vem do
 * gap da coluna (`DENSITY_TOKENS[d].gap`).
 */

import React from 'react';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  CardActionsMenu,
  Tooltip,
} from '@/shared/components/ui';
import type { CardAction } from '@/shared/components/ui';
import { useKanbanDensity } from '@/shared/components/kanban/KanbanDensityContext';
import { Icon } from '@/shared/icons';
import type { TicketAlertClockConfig, TicketKanbanCard } from '@troia-v3/types';
import type { PopulatedTicket } from '../types';
import { TICKET_PRIORITY } from '../types';
import { computeStageClock, formatStageAge } from '../utils';
import { TICKET_ALERT_DEFAULTS } from '@/modules/helpdesk/pipelines/components/pipelineAlertHelpers';

/**
 * Ticket Card Action Types
 */
export type TicketCardAction = 'view' | 'start' | 'assign' | 'resolve' | 'close' | 'delete';

/**
 * Props do TicketCard
 */
export interface TicketCardProps {
  /**
   * Dados do ticket — aceita o `TicketKanbanCard` enxuto do board windowed
   * (SP3) além do `PopulatedTicket` completo (table view / outros consumidores).
   * O card só lê campos presentes nos dois shapes.
   */
  ticket: PopulatedTicket | TicketKanbanCard;
  /** Callback quando uma ação do menu ••• é selecionada. */
  onAction?: (action: TicketCardAction, ticket: PopulatedTicket | TicketKanbanCard) => void;
  /**
   * @deprecated Aceita e ignorada. O desenho aprovado (seção 1 do spec) exige que
   * TODOS os cards mostrem o rosto do responsável **quando existir** — e a página
   * hoje passa `showAssignee={selectedUserId === 'all'}`, ou seja, filtrar o board
   * por operador escondia justamente o rosto de quem está atendendo. A prop
   * continua na interface só para não obrigar outros chamadores a mudar; sai
   * quando a página for tocada por outro motivo. Mesma decisão do card de
   * conversa do Atendimento.
   */
  showAssignee?: boolean;
  /** Indica se o ticket está selecionado */
  isSelected?: boolean;
  /** Renderiza APENAS o menu ••• (rowmore da table view) — sem o card. */
  menuOnly?: boolean;
  /**
   * Nome da etapa atual — a linha "Etapa" do card de Detalhes. O board enxuto
   * (`TicketKanbanCard`) só traz `stageId`; quem resolve o nome é a página, que
   * já tem `stages` de `useStagesByPipelineId`.
   */
  stageName?: string;
  /**
   * Limiares de ESTAGNAÇÃO resolvidos para a etapa deste chamado (cascata
   * etapa → pipeline → sistema, tarefa C4). É contra eles que o corpo 'normal'
   * pinta o trilho — NÃO contra `sla.clocks`, que mede contrato e não estagnação.
   *
   * Ausente (tabela, storybook, etapa ainda não carregada) → o card cai em
   * `TICKET_ALERT_DEFAULTS.timeInStage` (o `SYSTEM_TICKET_ALERT_DEFAULTS` de
   * `@troia-v3/types`: habilitado, 24h/72h) e DESENHA o trilho normalmente —
   * o mesmo piso da cascata do backend, para o card não ficar sem indicador
   * enquanto a etapa não chega. O trilho só some quando o relógio resolvido
   * vem `enabled: false` ou a data de entrada na etapa é inválida
   * (`computeStageClock` devolve `level: 'off'`).
   */
  stageAlertClock?: TicketAlertClockConfig;
}

/** Primeira letra do nome, em caixa alta — lettering do avatar. */
const initialOf = (name?: string): string => name?.trim().charAt(0).toUpperCase() || '?';

/**
 * Avatar do CONTATO — CÍRCULO (`.hk-round` do mockup). A forma é o que diz o
 * papel (círculo = contato, squircle = responsável); a cor não, porque o
 * `AvatarFallback` shared é sempre slate #64748b por regra do projeto
 * (`shared/components/ui/avatar.tsx:41`).
 */
const ContactAvatar: React.FC<{ name?: string; picture?: string; size: 20 | 28 }> = ({
  name,
  picture,
  size,
}) => (
  <Avatar className={size === 28 ? 'w-7 h-7 flex-none' : 'w-5 h-5 flex-none'}>
    <AvatarImage src={picture} alt={name ?? 'Contato'} />
    <AvatarFallback>
      <span style={{ fontSize: size === 28 ? 11 : 9, fontWeight: 600 }}>{initialOf(name)}</span>
    </AvatarFallback>
  </Avatar>
);

/** Avatar do RESPONSÁVEL — squircle (`border-radius: 32%` do §9 da fundação). */
const AssigneeAvatar: React.FC<{ name?: string; picture?: string; size: 20 | 24 }> = ({
  name,
  picture,
  size,
}) => (
  <Avatar className={size === 24 ? 'w-6 h-6 flex-none' : 'w-5 h-5 flex-none'} isSquircle>
    <AvatarImage src={picture} alt={name ?? 'Responsável'} isSquircle />
    <AvatarFallback isSquircle>
      <span style={{ fontSize: size === 24 ? 10 : 9, fontWeight: 600 }}>{initialOf(name)}</span>
    </AvatarFallback>
  </Avatar>
);

/**
 * Sem responsável — `.c3-avatar--empty` do mockup: fundo transparente, contorno
 * TRACEJADO de 1px em `--theme-input-border`, ícone de linha de 12px
 * (`.c3-icon--xs`). Tooltip shared no lugar do `title` nativo (regra travada).
 */
const EmptyAssignee: React.FC<{ size: 20 | 24 }> = ({ size }) => (
  <Tooltip content="Sem responsável">
    <span
      className={`${size === 24 ? 'w-6 h-6' : 'w-5 h-5'} rounded-[32%] inline-flex items-center justify-center flex-none`}
      style={{
        border: '1px dashed var(--theme-input-border)',
        color: 'var(--theme-muted-text)',
      }}
      aria-label="Sem responsável"
    >
      <Icon name="user" className="w-3 h-3" />
    </span>
  </Tooltip>
);

/** Rótulo da grade do card de Detalhes (`.hk1-lb`): 11/600/0.06em, uppercase. */
const GridLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <dt
    className="m-0 uppercase min-w-0 truncate"
    style={{
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: '0.06em',
      lineHeight: '18px',
      color: 'var(--theme-muted-text)',
    }}
  >
    {children}
  </dt>
);

/** Valor da grade (`.hk1-vl`): 13, line-height 18px, cor de corpo do card. */
const GRID_VALUE_STYLE: React.CSSProperties = {
  fontSize: 13,
  lineHeight: '18px',
  color: 'var(--theme-card-text)',
};

/**
 * Card de ticket para o Kanban Help Desk
 */
export const TicketCard: React.FC<TicketCardProps> = ({
  ticket,
  onAction,
  isSelected = false,
  menuOnly = false,
  stageName,
  stageAlertClock,
}) => {
  // Hook SEMPRE antes de qualquer return (o `menuOnly` sai cedo, mais abaixo).
  const density = useKanbanDensity();
  // As TRÊS densidades do Helpdesk existem (TICKET_CARD_DENSITIES):
  //   'detailed' → 01 "Ficha do cliente"  ·  'normal' → 07 "Relógio de etapa"
  //   'compact'  → 05 "Linha de fila"
  // O default do contexto fora do provider é 'normal'
  // (`DEFAULT_CARD_DENSITY`, shared/components/kanban/density.ts); o default do
  // MÓDULO continua 'detailed' e é decidido em useTicketsPreferences. Valor
  // desconhecido cai em 'detailed'.
  const variant: 'detailed' | 'normal' | 'compact' =
    density === 'compact' ? 'compact' : density === 'normal' ? 'normal' : 'detailed';

  const priority = TICKET_PRIORITY[ticket.priority as keyof typeof TICKET_PRIORITY];
  const priorityColor = priority?.color ?? 'var(--theme-muted-text)';

  // "TCK-2026-000187" → "#187" (número completo fica no Tooltip).
  const shortNumber = String(
    parseInt(ticket.ticketNumber?.split('-').pop() || '', 10) || ticket.ticketNumber,
  );

  const contactName = ticket.contact?.name;
  const stageLabel =
    stageName ?? ('stage' in ticket ? ticket.stage?.name : undefined) ?? 'Sem etapa';
  // Idade na etapa: `stageEnteredAt` é gravado na criação e a cada movimentação;
  // o fallback para `createdAt` já vem resolvido do backend (mapToKanbanCard),
  // mas o `PopulatedTicket` da tabela pode não ter — segundo fallback aqui.
  const stageAge = formatStageAge(ticket.stageEnteredAt ?? ticket.createdAt);
  const updatedAge = formatStageAge(ticket.updatedAt ?? ticket.createdAt);
  // Relógio de estagnação do corpo 'normal'. `TICKET_ALERT_DEFAULTS` só entra
  // quando a etapa ainda não trouxe limiar resolvido — nunca sobrescreve o que
  // veio do servidor.
  const stageClock = computeStageClock(
    ticket.stageEnteredAt ?? ticket.createdAt,
    stageAlertClock ?? TICKET_ALERT_DEFAULTS.timeInStage,
  );

  // Build actions for CardActionsMenu
  const buildActions = (): CardAction[] => {
    const actions: CardAction[] = [
      {
        label: 'Ver Detalhes',
        icon: 'eye',
        onClick: () => onAction?.('view', ticket),
      },
    ];

    const cat = ticket.statusCategory;

    // Iniciar: only for new or waiting tickets
    if (cat === 'new' || cat === 'waiting') {
      actions.push({
        label: 'Iniciar',
        icon: 'play',
        onClick: () => onAction?.('start', ticket),
      });
    }

    actions.push({
      label: 'Transferir',
      icon: 'refresh',
      onClick: () => onAction?.('assign', ticket),
    });

    if (cat !== 'resolved' && cat !== 'closed') {
      actions.push({
        label: 'Resolver',
        icon: 'check-circle',
        onClick: () => onAction?.('resolve', ticket),
      });
    }

    // Escada novo→resolvido→fechado (decisão do dono 2026-07-13): "Fechar" só
    // aparece após resolvido — mesmo gate do header da tela do ticket.
    if (cat === 'resolved') {
      actions.push({
        label: 'Fechar',
        icon: 'x-circle',
        onClick: () => onAction?.('close', ticket),
      });
    }

    actions.push({
      label: 'Excluir',
      icon: 'trash',
      onClick: () => onAction?.('delete', ticket),
      variant: 'danger',
    });

    return actions;
  };

  // rowmore da table view (.td-act do mockup): só o menu de ações
  if (menuOnly) {
    return (
      <CardActionsMenu
        actions={buildActions()}
        size="sm"
        data-testid={`ticket-row-menu-${ticket.id}`}
      />
    );
  }

  // `.c3-card`: flat, borda de 1px, padding 14, radius 12. O hover escurece a
  // BORDA (nunca sombra — regra dura 1 da fundação). Sem `mb-2`: o passo entre
  // cards vem do gap da coluna.
  const containerStyle: React.CSSProperties = {
    padding: 14,
    backgroundColor: isSelected
      ? 'var(--theme-primary-background)'
      : 'var(--theme-card-background)',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: isSelected ? 'var(--theme-primary)' : 'var(--theme-card-border)',
  };
  const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isSelected) e.currentTarget.style.borderColor = 'var(--theme-input-border)';
  };
  const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isSelected) e.currentTarget.style.borderColor = 'var(--theme-card-border)';
  };

  // ── COMPACTO — mockup Helpdesk 05 "Linha de fila" ─────────────────────────
  // Duas linhas: [dot 8 · assunto · par de avatares] e [contato · tempo na etapa].
  // Gap interno 4px; padding 14 (o mockup ORIGINAL usava 8/12 e foi CORRIGIDO
  // para 14 na comparação — é o padding que alinha com os vizinhos da coluna).
  if (variant === 'compact') {
    return (
      <div
        data-testid={`ticket-card-${ticket.id}`}
        className="rounded-xl relative flex flex-col gap-1 transition-colors"
        style={containerStyle}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* `.hk5-line` — min-height 24px = avatar de 20 + 2px de anel de cada lado */}
        <div className="flex items-center gap-2 min-w-0" style={{ minHeight: 24 }}>
          <span
            className="flex-none rounded-full"
            style={{ width: 8, height: 8, backgroundColor: priorityColor }}
            role="img"
            aria-label={priority?.label ?? 'Sem prioridade'}
          />
          <span
            className="flex-1 min-w-0 truncate font-medium"
            style={{ fontSize: 13, color: 'var(--theme-text)' }}
          >
            {ticket.title}
          </span>
          {/* `.hk5-pair` — contato (círculo) + responsável (squircle) sobrepostos
              em -5px, com anel de 2px na cor do card. O anel vai no INVÓLUCRO,
              não no avatar: o `isSquircle` aplica `mask-image`, e a máscara
              recortaria o próprio box-shadow. */}
          <span className="inline-flex items-center flex-none">
            <ContactAvatar name={contactName} picture={ticket.contact?.picture} size={20} />
            {/* O par de rostos é o que este card tem no lugar de rótulo de papel
                (D-H6). Não há gate: o rosto do responsável aparece sempre que
                existir, e a ausência tem desenho próprio (`EmptyAssignee`). */}
            <span
              className="inline-flex flex-none"
              style={{
                marginLeft: -5,
                borderRadius: '32%',
                boxShadow: '0 0 0 2px var(--theme-card-background)',
              }}
            >
              {ticket.assignee ? (
                <AssigneeAvatar
                  name={ticket.assignee.name}
                  picture={ticket.assignee.picture}
                  size={20}
                />
              ) : (
                <EmptyAssignee size={20} />
              )}
            </span>
          </span>
        </div>

        {/* `.hk5-micro` — 11px muted, padding-left 16 (dot 8 + gap 8) para o
            texto nascer no mesmo eixo do assunto da linha de cima. */}
        <div
          className="flex items-center min-w-0"
          style={{ gap: 4, paddingLeft: 16, fontSize: 11, color: 'var(--theme-muted-text)' }}
        >
          <span className="min-w-0 truncate">{contactName ?? 'Sem contato'}</span>
          <span className="flex-none">·</span>
          <span className="flex-none" style={{ fontVariantNumeric: 'tabular-nums' }}>
            {stageAge}
          </span>
        </div>
      </div>
    );
  }

  // ── NORMAL — mockup Helpdesk 07 "Relógio de etapa" (helpdesk.html:939-956) ──
  // Quatro blocos empilhados com gap 8 (`.c3-card` gap): [etapa · tempo],
  // trilho de 3px, assunto em 13/600 clamp2, e [pill do contato · responsável].
  // MEDE ESTAGNAÇÃO (`stageEnteredAt` contra `alertConfig`), nunca prazo de SLA.
  if (variant === 'normal') {
    return (
      <div
        data-testid={`ticket-card-${ticket.id}`}
        className="rounded-xl relative flex flex-col gap-2 transition-colors"
        style={containerStyle}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* `.c3-row--between` — `.hd2-label` à esquerda, `.hd2-stage-time` à direita */}
        <div className="flex items-center justify-between gap-2 min-w-0">
          <span
            className="uppercase min-w-0 truncate"
            style={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.08em',
              lineHeight: 1,
              color: 'var(--theme-muted-text)',
            }}
          >
            {stageLabel}
          </span>
          <span
            className="flex-none"
            style={{
              fontSize: 12,
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
              color: stageClock.toneFg,
            }}
            data-testid={`ticket-card-stage-clock-${ticket.id}`}
          >
            {stageClock.label}
          </span>
        </div>

        {/* `.c3-track` — 3px, radius full, fundo afundado. Some quando o relógio
            está desligado: trilho sem limiar seria decoração colorida, e a
            fundação proíbe acento decorativo (cards-v3.css:378-380). */}
        {stageClock.level !== 'off' && (
          <div
            style={{
              height: 3,
              borderRadius: 9999,
              backgroundColor: 'var(--theme-surface-sunken)',
              overflow: 'hidden',
              minWidth: 0,
            }}
            role="img"
            aria-label={`Tempo na etapa: ${stageClock.percent}% do limite`}
            data-testid={`ticket-card-stage-track-${ticket.id}`}
          >
            <div
              style={{
                width: `${stageClock.percent}%`,
                height: '100%',
                borderRadius: 'inherit',
                backgroundColor: stageClock.toneSolid,
              }}
            />
          </div>
        )}

        {/* `.c3-clamp2 .hd2-title-13` — 13/600, tight, 1.4, duas linhas */}
        <p
          className="m-0 line-clamp-2 min-w-0"
          style={{
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: '-0.02em',
            lineHeight: 1.4,
            color: 'var(--theme-text)',
          }}
        >
          {ticket.title}
        </p>

        {/* `.c3-row--between` — pill do contato à esquerda, responsável à direita */}
        <div className="flex items-center justify-between gap-2 min-w-0">
          <span
            className="inline-flex items-center min-w-0"
            style={{
              gap: 6,
              maxWidth: '100%',
              padding: '2px 10px 2px 2px',
              border: '1px solid var(--theme-input-border)',
              borderRadius: 9999,
              backgroundColor: 'var(--theme-card-background)',
              fontSize: 12,
              color: 'var(--theme-card-text)',
            }}
          >
            <ContactAvatar name={contactName} picture={ticket.contact?.picture} size={20} />
            <span className="min-w-0 truncate">{contactName ?? 'Sem contato'}</span>
          </span>
          {/* `title=` do mockup vira Tooltip shared — `title` nativo é proibido */}
          {ticket.assignee ? (
            <Tooltip content={`Responsável · ${ticket.assignee.name}`}>
              <span className="flex-none inline-flex">
                <AssigneeAvatar
                  name={ticket.assignee.name}
                  picture={ticket.assignee.picture}
                  size={24}
                />
              </span>
            </Tooltip>
          ) : (
            <EmptyAssignee size={24} />
          )}
        </div>
      </div>
    );
  }

  // ── DETALHES — mockup Helpdesk 01 "Ficha do cliente" ──────────────────────
  // A pessoa é o título; o chamado vira atributo dela numa grade rotulada.
  return (
    <div
      data-testid={`ticket-card-${ticket.id}`}
      className="group rounded-xl relative flex flex-col gap-2 transition-colors"
      style={containerStyle}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Cabeçalho: avatar 28 do contato + nome (14/600) + sub-linha + kebab */}
      <div className="flex items-center gap-2 min-w-0">
        <ContactAvatar name={contactName} picture={ticket.contact?.picture} size={28} />
        <div className="flex-1 min-w-0">
          <div
            className="truncate font-semibold"
            style={{
              fontSize: 14,
              lineHeight: 1.35,
              letterSpacing: '-0.02em',
              color: 'var(--theme-text)',
            }}
          >
            {contactName ?? 'Sem contato'}
          </div>
          {/* `.c3-card__sub` (12 muted, margin-top 2) — número curto + categoria */}
          <Tooltip content={`Chamado ${ticket.ticketNumber}`}>
            <div
              className="flex items-center min-w-0"
              style={{ gap: 6, marginTop: 2, fontSize: 12, color: 'var(--theme-muted-text)' }}
              tabIndex={0}
            >
              <span className="flex-none" style={{ fontVariantNumeric: 'tabular-nums' }}>
                #{shortNumber}
              </span>
              {ticket.category && (
                <>
                  <span className="flex-none">·</span>
                  <span className="min-w-0 truncate">{ticket.category}</span>
                </>
              )}
            </div>
          </Tooltip>
        </div>
        {/* `.c3-card--revealkebab`: espaço reservado, visível no hover/foco.
            O stopPropagation impede que o clique no menu abra o drawer pelo
            invólucro da coluna. */}
        <div
          className="flex-none -mr-1 -mt-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100"
          onClick={(e) => e.stopPropagation()}
        >
          <CardActionsMenu
            actions={buildActions()}
            size="sm"
            data-testid={`ticket-card-menu-${ticket.id}`}
          />
        </div>
      </div>

      {/* `.hk-bleed` — fio de cabelo que sangra os 14px de padding dos dois lados */}
      <div
        style={{ height: 1, margin: '0 -14px', backgroundColor: 'var(--theme-card-border)' }}
      />

      {/* `.hk1-grid` — grade 88px / resto, gaps de 8, alinhada ao topo */}
      <dl
        className="grid m-0 min-w-0"
        style={{
          gridTemplateColumns: '88px minmax(0, 1fr)',
          columnGap: 8,
          rowGap: 8,
          alignItems: 'start',
        }}
      >
        <GridLabel>Assunto</GridLabel>
        {/* `.hk1-vl--subject`: 13/500 na cor de título, 2 linhas, line-height 1.38 */}
        <dd
          className="m-0 min-w-0 line-clamp-2"
          style={{ fontSize: 13, fontWeight: 500, lineHeight: 1.38, color: 'var(--theme-text)' }}
        >
          {ticket.title}
        </dd>

        <GridLabel>Prioridade</GridLabel>
        <dd className="m-0 min-w-0 flex items-center" style={{ ...GRID_VALUE_STYLE, gap: 6 }}>
          <span
            className="flex-none rounded-full"
            style={{ width: 6, height: 6, backgroundColor: priorityColor }}
          />
          <span className="min-w-0 truncate">{priority?.label ?? 'Sem prioridade'}</span>
        </dd>

        <GridLabel>Etapa</GridLabel>
        <dd className="m-0 min-w-0 truncate" style={GRID_VALUE_STYLE}>
          {stageLabel}
        </dd>

        {/* Linha SEMPRE presente: o rosto do responsável aparece quando existir,
            e "Sem responsável" é informação, não ausência de informação. Não
            existe gate por filtro de operador — filtrar o board por operador não
            pode esconder quem está atendendo. */}
        <GridLabel>Responsável</GridLabel>
        <dd className="m-0 min-w-0 flex items-center" style={{ ...GRID_VALUE_STYLE, gap: 6 }}>
          {ticket.assignee ? (
            <AssigneeAvatar
              name={ticket.assignee.name}
              picture={ticket.assignee.picture}
              size={20}
            />
          ) : (
            <EmptyAssignee size={20} />
          )}
          <span className="min-w-0 truncate">{ticket.assignee?.name ?? 'Sem responsável'}</span>
        </dd>
      </dl>

      {/* `.hk1-foot` — 11px muted */}
      <div style={{ fontSize: 11, color: 'var(--theme-muted-text)' }}>
        Atualizado {updatedAge}
      </div>
    </div>
  );
};
